import React from "react";
import { components as ReactSelectComponents } from "react-select";

import { captureException } from "@sentry/browser";

import CreatableSelector, {
	type ControlProps,
	type SelectorProps,
	type SingleValue,
	type SingleValueProps,
	type ValueContainerProps,
} from "~/ui/selectors/CreatableSelector";

import {
	guessTimeFromString,
	isEqualTimeValue,
	makeTimeOption,
	type TimeOption,
	type TimeValue,
	timeValueToDate,
	useTimeOptions,
} from "../hooks/useTimeOptions";

import styles from "./TimeSelector.module.css";

const BlankComponent: React.FC = () => null;

const SingleValueComponent: React.ComponentType<SingleValueProps<TimeOption, false>> = props => {
	let timeLabel = props.data.label;

	if (props.data.timeValue.minute === 0) {
		timeLabel = new Intl.DateTimeFormat("en-US", { hour: "numeric" }).format(timeValueToDate(props.data.timeValue));
	}

	return (
		<ReactSelectComponents.SingleValue {...props} className={styles.singleValue}>
			{timeLabel}
		</ReactSelectComponents.SingleValue>
	);
};

const ValueContainerComponent: React.ComponentType<ValueContainerProps<TimeOption, false>> = ({
	children,
	...rest
}) => (
	<ReactSelectComponents.ValueContainer {...rest} className={styles.valueContainer}>
		{children}
	</ReactSelectComponents.ValueContainer>
);

const ControlComponent: React.ComponentType<ControlProps<TimeOption, false>> = ({ children, ...rest }) => (
	<ReactSelectComponents.Control {...rest} className={styles.control}>
		{children}
	</ReactSelectComponents.Control>
);

interface TimeSelectorProps {
	time: TimeValue;
	setTime: (newTime: TimeValue) => void;
	timeToCompare?: TimeValue;
}

export function TimeSelector({ time, setTime, timeToCompare }: TimeSelectorProps) {
	const options = useTimeOptions(timeToCompare);

	const onChange = React.useCallback(
		(newOption: SingleValue<TimeOption>) =>
			newOption && setTime({ hour: newOption.timeValue.hour, minute: newOption.timeValue.minute }),
		[setTime],
	);

	const isValidNewOption = React.useCallback<NonNullable<SelectorProps<TimeOption>["isValidNewOption"]>>(
		(inputValue, value, options) => {
			const potentialOption = guessTimeFromString(inputValue);
			if (potentialOption === null) return false;

			return !options.find(option => isEqualTimeValue(potentialOption.timeValue, (option as TimeOption).timeValue));
		},
		[],
	);

	const getNewOptionData = React.useCallback<NonNullable<SelectorProps<TimeOption>["getNewOptionData"]>>(
		inputValue => {
			const guessedTime = guessTimeFromString(inputValue);

			// Should only be called after isValidNewOption, according to react-select. This is to satisfy typescript
			if (guessedTime === null) {
				captureException(`Attempting to create invalid time option. Something is really wrong.`, {
					extra: { inputValue },
				});
				return options[0];
			}

			return guessedTime;
		},
		[options],
	);

	const filterOption = React.useCallback<NonNullable<SelectorProps<TimeOption>["filterOption"]>>(
		(option, inputValue) => {
			if (inputValue === "") return true;

			const guessedTime = guessTimeFromString(inputValue);
			if (guessedTime == null) return false;

			return isEqualTimeValue(guessedTime.timeValue, option.data.timeValue);
		},
		[],
	);

	const formatCreateLabel = React.useCallback<NonNullable<SelectorProps<TimeOption>["formatCreateLabel"]>>(
		inputValue => guessTimeFromString(inputValue)?.label,
		[],
	);

	const isOptionSelected = React.useCallback<NonNullable<SelectorProps<TimeOption>["isOptionSelected"]>>(
		(option, selectValue) => isEqualTimeValue(option.timeValue, selectValue[0].timeValue),
		[],
	);

	return (
		<CreatableSelector<TimeOption>
			condensed
			isSearchable
			onChange={onChange}
			value={makeTimeOption({ hour: time.hour, minute: time.minute })}
			options={options}
			isValidNewOption={isValidNewOption}
			getNewOptionData={getNewOptionData}
			filterOption={filterOption}
			noOptionsMessage={React.useCallback(() => "Invalid time", [])}
			formatCreateLabel={formatCreateLabel}
			maxMenuHeight={175}
			isOptionSelected={isOptionSelected}
			components={{
				SingleValue: SingleValueComponent,
				ValueContainer: ValueContainerComponent,
				Control: ControlComponent,
				IndicatorsContainer: BlankComponent,
			}}
		/>
	);
}
